import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useInView } from 'react-intersection-observer';

import Text from '@hiredigital/ui/Text/Text';
import { toReadableDate } from '@hiredigital/lib/helpers/marketingDate';

import Link from '@components/Link';
import Header from '@components/Header/MinimalHeader';
import SectionContainer from '@components/Section/Container';
import Section from '@components/Section/Section';
import BaseLayout from '@components/Layout/BaseLayout';
import Footer from '@components/Footer/Footer';
import JobDescriptionCta from '@components/PageComponents/CallToAction/JobDescriptionCta';
import Tags from '@components/SkillSet/Tags';
import TableContents from '@components/Blog/TableContents';

import TextContent, { extractHeadings, slugPrefixMap } from '@components/TextContent/TextContent';

import { getJobDescriptionPaths, getJobDescriptionPage } from '@cms/jobDescriptions';
import { getUsers, mergeCardUsersData, limitUsersData } from '@helpers/users';
import { skipStaticBuild } from '@helpers/utils';

import Styles from './Styles.module.scss';

const TalentSlider = dynamic(() => import('@components/TalentSlider/TalentSlider'), {
  ssr: false,
});

const JobDescriptionPage = ({
  featuredImage,
  title,
  metaTitle,
  metaDescription,
  keywords,
  sections,
  description,
  relatedResources,
  ctaTitle,
  ctaDescription,
  footer,
  slug,
  createdAt,
  updatedAt,
  readableDate,
  analyticsData = {},
  cardUsers,
}) => {
  const router = useRouter();

  const updatedAnalyticsData = {
    ...router.query,
    ...analyticsData,
  };
  const postUrl = `${process.env.NEXT_PUBLIC_SITE_URL}/job-descriptions/${slug}`;

  const [titleInView, setTitleInView] = useState(true);
  const [ref, inView, entry] = useInView({ threshold: 0, initialInView: true });
  useEffect(() => {
    setTitleInView(inView);
  }, [inView, entry]);

  const headings = extractHeadings(sections);
  const h2headings = headings.filter((heading) => heading.type == 'heading-two');

  return (
    <BaseLayout
      pageHead={{
        title: `${metaTitle || title} | Hire Digital`,
        meta: [
          {
            name: 'description',
            content: metaDescription || description,
          },
          { name: 'keywords', content: keywords.join(', ') },
          { name: 'author', content: 'Hire Digital' },

          {
            property: 'og:title',
            content: `${metaTitle || title} | Hire Digital`,
          },
          { property: 'og:type', content: 'article' },
          { property: 'og:url', content: postUrl },
          { property: 'og:publisher', content: process.env.NEXT_PUBLIC_SITE_URL },
          { property: 'og:image:width', content: '1200px' },
          { property: 'og:image:height', content: '630px' },
          { property: 'article:published_time', content: createdAt },
          { property: 'article:modified_time', content: updatedAt },
          { property: 'article:tag', content: keywords.join(', ') },
          { property: 'article:section', content: 'Job Descriptions' },
        ],
      }}
      analyticsData={updatedAnalyticsData}>
      <Header urlPath={router.query} isBlack />
      <SectionContainer className={Styles.titleContainer}>
        <Section className={Styles.titleSection}>
          <div ref={ref} className={Styles.titleGroup}>
            <Text element='h1' className={Styles.title}>
              {title}
            </Text>
            <p className={Styles.date}>{`Last updated on ${readableDate}`}</p>
          </div>
          <Text className={Styles.description}>{description}</Text>
        </Section>
      </SectionContainer>
      <SectionContainer className={Styles.contentContainer}>
        <Section className={Styles.contentSection}>
          <aside className={classNames(Styles.floatingContainer, Styles.left)} aria-hidden='true'>
            <div className={classNames(titleInView && Styles.inView, Styles.floatingSection)}>
              <div className={Styles.floatingTitle}>{title}</div>
              <TableContents list={h2headings} />
            </div>
          </aside>
          {cardUsers?.length > 0 && (
            <aside
              className={classNames(Styles.floatingContainer, Styles.right)}
              aria-hidden='true'>
              <div className={classNames(titleInView && Styles.inView, Styles.floatingSection)}>
                <TalentSlider items={cardUsers} />
              </div>
            </aside>
          )}
          {sections.map((section, index) =>
            TextContent({ section, index, updatedAnalyticsData, headings })
          )}
          <Tags tags={keywords} count={keywords.length} />
        </Section>
      </SectionContainer>

      {relatedResources.length ? (
        <SectionContainer className={Styles.nextContainer}>
          <Section className={Styles.nextSection}>
            <Text element='h3' className={Styles.sectionHeader}>
              Related Resources
            </Text>
            <div className={Styles.resourcesContainer}>
              {relatedResources.map((resource, ind) => {
                const slugPrefix = slugPrefixMap[resource.type] || '/';
                return (
                  <Link
                    href={`${slugPrefix}${resource.slug}`}
                    className={Styles.resourceItem}
                    key={ind}>
                    <p className={Styles.resourceTitle}>{resource.title}</p>
                    <p className={Styles.resourceDescription}>{resource.description}</p>
                  </Link>
                );
              })}
            </div>
          </Section>
        </SectionContainer>
      ) : (
        ''
      )}

      <JobDescriptionCta
        title={
          ctaTitle ||
          'Work with the most qualified talent in Marketing, Creative, and Software Development'
        }
        description={ctaDescription || ''}
        analyticsData={updatedAnalyticsData}
      />

      <Footer footer={footer} />
    </BaseLayout>
  );
};

export default JobDescriptionPage;

export const getStaticProps = async ({ params, preview }) => {
  const { page } = await getJobDescriptionPage(`${params.slug}`, preview);
  if (!page) {
    return {
      notFound: true,
      revalidate: 5000000,
    };
  }

  const users = page.featuredTalents;
  delete page.featuredTalents;
  const userUuids = users.filter(({ uuid }) => !!uuid).map(({ uuid }) => uuid);
  let apiUsers = [];
  if (userUuids.length) {
    apiUsers = await getUsers(userUuids);
  }
  const cardUsers = limitUsersData(mergeCardUsersData([], users, apiUsers));

  const analyticsData = {};
  const readableDate = toReadableDate(page.updatedAt);
  return {
    props: { ...page, cardUsers, analyticsData, readableDate },
    revalidate: 5000000,
  };
};

export const getStaticPaths = async () => {
  if (skipStaticBuild) return { paths: [], fallback: 'blocking' };

  const { pages } = await getJobDescriptionPaths();
  const paths = pages.map((page) => {
    return {
      params: {
        slug: page.slug,
      },
    };
  });

  return {
    paths,
    fallback: 'blocking',
  };
};
